import React from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { ListBulletIcon } from '@heroicons/react/24/outline';
import {
  IEpisodeOutline
} from '@packages/global';
import OutlineChatPanel from './OutlineChatPanel';

interface EpisodeOutlineListProps {
  projectId: string;
  outlines: IEpisodeOutline[];
  loading?: boolean;
  onAdd: () => void;
  onEdit: (outline: IEpisodeOutline) => void;
  onDelete: (outline: IEpisodeOutline) => void;
  onGenerateScript: (outline: IEpisodeOutline) => void;
  onRefresh: () => void;
}

export default function EpisodeOutlineList({
  projectId,
  outlines,
  loading,
  onAdd,
  onEdit,
  onDelete,
  onGenerateScript,
  onRefresh
}: EpisodeOutlineListProps) {
  const { t } = useTranslation(['projectSettings', 'common']);
  if (loading) {
    return (
      <div className="h-full flex flex-col gap-5 min-h-0">
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="h-7 w-32 bg-border-subtle animate-pulse rounded" />
          <div className="flex gap-3">
            <div className="h-9 w-32 bg-border-subtle animate-pulse rounded-lg" />
            <div className="h-9 w-24 bg-border-subtle animate-pulse rounded-lg" />
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-3 overflow-y-auto min-h-0">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-28 bg-bg-surface rounded-xl animate-pulse flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex gap-4 min-h-0">
      {/* 左侧列表区 */}
      <div className="flex-1 flex flex-col gap-5 min-h-0">
        {/* Header */}
        <div className="flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <ListBulletIcon className="w-5 h-5 text-primary" />
            <h2 className="text-lg font-semibold text-gray-50">{t('outline.title')}</h2>
          </div>
          <button
            onClick={onAdd}
            className="flex items-center gap-2 px-4 py-2 border border-border-default hover:bg-bg-elevated text-gray-300 hover:text-gray-50 rounded-lg text-sm transition-colors cursor-pointer"
          >
            <PlusIcon className="w-4 h-4" />
            {t('outline.addEpisode')}
          </button>
        </div>

        {/* List */}
        <div className="flex-1 flex flex-col gap-3 overflow-y-auto min-h-0">
          {outlines.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
              <ListBulletIcon className="w-12 h-12 mb-3" />
              <p>{t('outline.empty')}</p>
              <p className="text-sm mt-1">{t('outline.emptyHint')}</p>
            </div>
          ) : (
            outlines.map((outline) => (
              <OutlineCard
                key={outline.id}
                outline={outline}
                onEdit={() => onEdit(outline)}
                onDelete={() => onDelete(outline)}
                onGenerateScript={() => onGenerateScript(outline)}
              />
            ))
          )}
        </div>
      </div>

      {/* 右侧 AI 聊天面板 */}
      <div className="w-[400px] flex-shrink-0 min-h-0">
        <OutlineChatPanel
          projectId={projectId}
          onOutlinesChange={onRefresh}
        />
      </div>
    </div>
  );
}

interface OutlineCardProps {
  outline: IEpisodeOutline;
  onEdit: () => void;
  onDelete: () => void;
  onGenerateScript: () => void;
}

function OutlineCard({ outline, onEdit, onDelete, onGenerateScript }: OutlineCardProps) {
  const { t } = useTranslation(['projectSettings']);
  const hasScript = outline.generatedScriptId ? true : false;

  return (
    <div className="bg-bg-surface rounded-xl border border-border-subtle p-4 hover:border-border-default transition-colors">
      <div className="flex flex-col gap-3">
        {/* Header Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Episode Number Badge */}
            <div className="flex items-center justify-center w-8 h-8 bg-primary/20 text-primary rounded-lg text-sm font-semibold">
              {outline.episodeNumber}
            </div>
            <h3 className="text-gray-50 font-medium">{outline.title}</h3>
            {hasScript && (
              <span className="px-2 py-0.5 text-xs bg-success/20 text-success rounded">
                {t('outline.scriptGenerated')}
              </span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={onGenerateScript}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer ${hasScript
                ? 'bg-bg-elevated hover:bg-border-subtle text-gray-300 hover:text-gray-50'
                : 'bg-bg-elevated hover:bg-border-subtle text-primary'
                }`}
            >
              <SparklesIcon className="w-4 h-4" />
              {hasScript ? t('outline.viewScript') : t('outline.generateScript')}
            </button>
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-gray-50 transition-colors cursor-pointer"
            >
              <PencilIcon className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-gray-400 hover:text-error transition-colors cursor-pointer"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Synopsis */}
        <p className="text-sm text-gray-300 leading-relaxed">{outline.synopsis}</p>
      </div>
    </div>
  );
}
